import React, { useContext } from "react";
import PropTypes from "prop-types";
import moment from "moment";

import Config from "@skin_config";
import { betTypeNameMap, gameNameSportAliasMap, mapCasinoTicketNestedBets, mapCasinoTicketSelections } from "@tickets/helpers/mappers";
import { PreferencesContext } from "@common/contexts/preferences";
import TicketSkeleton from "@common/components/ticketSkeleton";
import { useTranslation } from "@common/hooks/translation";
import OddConverter from "@common/helpers/oddConverter";
import { addSpaceBeforeEachCapital } from '@common/helpers';
import Money from "@app/dev/terminal/components/money";

import { BetStatus } from "../components/betStatus";
import TicketDetailsCasinoSelection from "./selection";
import { getCasinoTicketIframeUrl } from "@tickets/helpers/getCasinoTicketIframeUrl";
import { PascalBetTypes } from "@tickets/services/casinoMiddleware/constants";

const TicketDetailsCasino = ({ ticket, showCasinoIframe }) => {
  const t = useTranslation();
  const [{ oddsFormat, preferences }] = useContext(PreferencesContext);

  const { dataFromSwarm, dataFromCasino } = ticket;
  const selectionList = mapCasinoTicketSelections({ casinoInfo: dataFromCasino });
  const gameName = dataFromCasino?.BetMultiplyInfo[0]?.GameName;
  const ticketType = dataFromCasino?.BetMultiplyInfo[0]?.AdditionalInfo?.ticketType
    || PascalBetTypes[dataFromCasino?.BetMultiplyInfo[0]?.BetType];

  const createdDate = dataFromSwarm.Created || dataFromCasino?.BetMultiplyInfo[0]?.CreateDate;

  const nestedBets = mapCasinoTicketNestedBets({ casinoInfo: dataFromCasino });
  const jackpotWinAmount = dataFromCasino?.TotalJackpotWinAmount;

  if (showCasinoIframe)
    return <iframe className="casino-ticket-iframe" src={getCasinoTicketIframeUrl(dataFromCasino.TransactionId)} />;

  return (
    <TicketSkeleton dark>
      <div className="ticket-section">
        <div className="ticket-info-row">
          <p>{t("Status")}</p>
          <BetStatus data={ticket} />
        </div>
        <div className="ticket-info-row">
          <p>{t("Id")}</p>
          <span>{dataFromCasino?.CasinoTransactionId}</span>
        </div>
        <div className="ticket-info-row">
          <p>{t("Date")}</p>
          <span>{moment(createdDate).format("DD/MM/YYYY HH:mm:ss")}</span>
        </div>
        <div className="ticket-info-row">
          <p>{t("Bet Type")}</p>
          <p className="ticket-info-bet-type-name">{ticketType ? t(betTypeNameMap[ticketType] || addSpaceBeforeEachCapital(ticketType)) : '-'}</p>
        </div>
        {gameName && (
          <div className="ticket-info-row">
            <p className="T-casino-game-name">
              {gameNameSportAliasMap[gameName] && <i className={`T-casino-game-sport-icon sport-icon sport-icon-${gameNameSportAliasMap[gameName]}`} />}
              {gameName}
            </p>
          </div>
        )}
      </div>

      {!!selectionList.length && (
        <div className="ticket-section">
          {selectionList.map((selection) => (
            <TicketDetailsCasinoSelection key={selection.id} data={selection} />
          ))}
        </div>
      )}

      {nestedBets.length ? (
        nestedBets.map((bet) => (
          <div className="ticket-section" key={bet.optionValue || ""}>
            <div className="ticket-info-row T-nested-bet-type">
              <p>{bet.betTypeName}</p>
              {bet.optionValue && <p>{bet.optionValue}</p>}
            </div>

            {bet.selectionList?.map((selection) => (
              <TicketDetailsCasinoSelection key={selection.id} data={selection} />
            ))}

            {bet.price && (
              <div className="ticket-info-row">
                <p>{t("Total odd")}</p>
                <p className="ticket-info-white">{OddConverter(Config, bet.price, oddsFormat)}</p>
              </div>
            )}

            <div className="ticket-info-row">
              <p>{t("Bet Amount")}</p>
              <p className="ticket-info-white">{bet.amount}</p>
            </div>

            <div className="ticket-info-row">
              <p>{t("Possible win")}</p>
              <p className="ticket-info-white">{bet.possibleWin}</p>
            </div>
          </div>
        ))
      ) : (
        <div className="ticket-section">
          {!!dataFromSwarm.price && (
            <div className="ticket-info-row">
              <p>{t("Total odd")}</p>
              <p>{OddConverter(Config, dataFromSwarm.price, preferences.oddsFormat) ?? "-"}</p>
            </div>
          )}

          {dataFromCasino?.Amount && (
            <div className="ticket-info-row">
              <p>{t("Stake")}</p>
              <p>
                <Money amount={dataFromCasino?.Amount + (dataFromCasino?.TaxAmountFromBet || 0)} />
              </p>
            </div>
          )}

          {!!dataFromCasino?.TaxAmountFromBet && (
            <>
              <div className="ticket-info-row">
                <p>{t("Tax on stake")}</p>
                <p>
                  <Money amount={dataFromCasino.TaxAmountFromBet} />
                </p>
              </div>
              <div className="ticket-info-row">
                <p>{t("Total stake")}</p>
                <p>
                  <Money amount={dataFromCasino.Amount} />
                </p>
              </div>
            </>
          )}

          {!!dataFromCasino?.PossibleTaxAmount && (
            <div className="ticket-info-row">
              <p>{t("Possible Tax amount")}</p>
              <p>
                <Money amount={dataFromCasino.PossibleTaxAmount} />
              </p>
            </div>
          )}

          <div className="ticket-info-row">
            <p>{t("Possible win")}</p>
            <p>
              <Money amount={dataFromCasino?.PossibleWinAmount} />
            </p>
          </div>

          {!!dataFromCasino?.PossibleTaxAmount && (
            <div className="ticket-info-row">
              <p>{t("Total win")}</p>
              <p>
                <Money amount={dataFromCasino.PossibleWinAmount - dataFromCasino.PossibleTaxAmount} />
              </p>
            </div>
          )}
          {!!dataFromCasino?.TotalWinAmount && (
            <div className="ticket-info-row">
              <p>{t("Won amount")}</p>
              <p className="win-amount">
                <Money amount={dataFromCasino.TotalWinAmount} />
              </p>
            </div>
          )}
          {!!jackpotWinAmount && (
            <div className="ticket-info-row">
              <div className="ticket-info-row-left-info">
                <i className="global-icon icon-jackpot" />
                <p className="ticket-info-row-label">{t("Jackpot")}</p>
              </div>
              <p className="win-amount">
                <Money amount={jackpotWinAmount} />
              </p>
            </div>
          )}
        </div>
      )}
    </TicketSkeleton>
  );
};

TicketDetailsCasino.propTypes = {
  ticket: PropTypes.object.isRequired,
};

export default TicketDetailsCasino;
